// =====================================================================
// Faz AO (2026-05-07) — Ürün Yorumları Moderasyon
// =====================================================================
// Owner müşteri yorumlarını onaylar/reddeder. Onaylanan yorumlar ürün
// sayfasında public olarak gösterilir.
//
// Endpoint:
//   GET /api/admin/reviews?status=pending|approved|rejected|all
//   PUT /api/admin/reviews/:id/approve
//   PUT /api/admin/reviews/:id/reject
//   DELETE /api/admin/reviews/:id
//
// Verified buyer kontrolü backend'de — admin görür ki yazan müşteri
// gerçekten ürünü almış (spam azaltır).
// =====================================================================
import React, { useEffect, useState } from "react";
import {
  Box, Card, Typography, Grid, Chip, CircularProgress, Alert, IconButton,
  Tooltip, Button, ToggleButton, ToggleButtonGroup, Avatar, Rating
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import API from "../services/api";
import toast from "react-hot-toast";

const fmtDate = (s) => s ? new Date(s).toLocaleString("tr-TR", {
  day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
}) : "—";

// Status → renk + Türkçe etiket
const STATUS_META = {
  pending:  { color: "#f59e0b", label: "Onay Bekliyor" },
  approved: { color: "#10b981", label: "Onaylandı" },
  rejected: { color: "#ef4444", label: "Reddedildi" }
};

export default function ReviewsModeration() {
  const [reviews, setReviews] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = () => {
    setLoading(true);
    API.get(`/api/admin/reviews?status=${filter}`)
      .then(r => {
        setReviews(r.data.reviews || []);
        setCounts(r.data.counts || {});
        setErr(null);
      })
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filter]);

  const approve = async (id) => {
    try {
      await API.put(`/api/admin/reviews/${id}/approve`);
      toast.success("Yorum onaylandı");
      load();
    } catch (e) { toast.error(e.response?.data?.error || e.message); }
  };

  const reject = async (id) => {
    const note = window.prompt("Red sebebi (opsiyonel — kullanıcıya gösterilmez):");
    if (note === null) return;
    try {
      await API.put(`/api/admin/reviews/${id}/reject`, { note });
      toast.success("Yorum reddedildi");
      load();
    } catch (e) { toast.error(e.response?.data?.error || e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu yorum tamamen silinsin mi?")) return;
    try {
      await API.delete(`/api/admin/reviews/${id}`);
      toast.success("Silindi");
      load();
    } catch (e) { toast.error(e.response?.data?.error || e.message); }
  };

  if (loading && reviews.length === 0) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  }

  const avgRating = Number(counts.avg_rating) || 0;

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Ürün Yorumları</Typography>
          <Typography variant="body2" color="text.secondary">
            Müşteri yorumları moderasyonu — onaylanan yorumlar ürün sayfasında görünür
          </Typography>
        </Box>
        <Tooltip title="Yenile"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>
      </Box>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {/* KPI özeti */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3, bgcolor: "#fffbeb" }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>BEKLEYEN</Typography>
            <Typography variant="h4" fontWeight={800} color="#f59e0b">{Number(counts.pending) || 0}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3, bgcolor: "#f0fdf4" }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>ONAYLANDI</Typography>
            <Typography variant="h4" fontWeight={800} color="#10b981">{Number(counts.approved) || 0}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3, bgcolor: "#fef2f2" }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>REDDEDİLDİ</Typography>
            <Typography variant="h4" fontWeight={800} color="#ef4444">{Number(counts.rejected) || 0}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>ORTALAMA PUAN</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="h4" fontWeight={800}>{avgRating.toFixed(1)}</Typography>
              <Rating value={avgRating} readOnly precision={0.1} size="small" />
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Filter */}
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup size="small" value={filter} exclusive onChange={(_, v) => v && setFilter(v)}>
          <ToggleButton value="pending">Bekleyen ({counts.pending || 0})</ToggleButton>
          <ToggleButton value="approved">Onaylı ({counts.approved || 0})</ToggleButton>
          <ToggleButton value="rejected">Red</ToggleButton>
          <ToggleButton value="all">Tümü</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Yorum kartları */}
      {reviews.length === 0 ? (
        <Alert severity={filter === "pending" ? "success" : "info"}>
          {filter === "pending" ? "✅ Onay bekleyen yorum yok" : "Bu kriterde yorum yok"}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {reviews.map(r => {
            const sm = STATUS_META[r.status] || STATUS_META.pending;
            return (
              <Grid item xs={12} md={6} key={r.id}>
                <Card sx={{
                  p: 2, borderRadius: 3, height: "100%",
                  borderLeft: `4px solid ${sm.color}`
                }}>
                  {/* Üst: ürün + status */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                    <Avatar src={r.product_image} variant="rounded" sx={{ width: 48, height: 48, bgcolor: "#f1f5f9" }}>📦</Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontSize={13} fontWeight={700} noWrap>{r.product_name}</Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Rating value={r.rating} readOnly size="small" />
                        <Typography fontSize={12} fontWeight={700}>{r.rating}/5</Typography>
                      </Box>
                    </Box>
                    <Chip label={sm.label} size="small" sx={{ bgcolor: sm.color, color: "white", fontWeight: 700 }} />
                  </Box>

                  {/* Title + comment */}
                  {r.title && (
                    <Typography fontWeight={700} fontSize={14} mb={0.5}>{r.title}</Typography>
                  )}
                  <Typography variant="body2" sx={{ fontSize: 13, color: "#1f2937", whiteSpace: "pre-wrap", mb: 1.5 }}>
                    {r.comment}
                  </Typography>

                  {/* Author + date */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                    <Box>
                      <Typography fontSize={12} fontWeight={600}>
                        {r.company_name || r.user_name}
                        <Chip label="✓ Doğrulanmış Alıcı" size="small" sx={{ ml: 1, height: 16, fontSize: 9, bgcolor: "#10b98122", color: "#10b981" }} />
                      </Typography>
                      <Typography variant="caption" color="text.disabled" fontSize={10}>
                        {r.user_email}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.disabled" fontSize={11}>
                      {fmtDate(r.created_at)}
                    </Typography>
                  </Box>

                  {/* Aksiyon butonları */}
                  {r.status === "pending" && (
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        variant="contained" size="small" startIcon={<CheckIcon />}
                        onClick={() => approve(r.id)}
                        sx={{ bgcolor: "#10b981", "&:hover": { bgcolor: "#059669" }, flex: 1 }}
                      >
                        Onayla
                      </Button>
                      <Button
                        variant="outlined" size="small" startIcon={<CloseIcon />}
                        onClick={() => reject(r.id)} color="error"
                        sx={{ flex: 1 }}
                      >
                        Reddet
                      </Button>
                      <IconButton size="small" onClick={() => remove(r.id)} sx={{ color: "#94a3b8" }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                  {r.status !== "pending" && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="caption" color="text.disabled">
                        Moderasyon: {fmtDate(r.moderated_at)}
                        {r.moderation_note && ` · "${r.moderation_note}"`}
                      </Typography>
                      <IconButton size="small" onClick={() => remove(r.id)} sx={{ color: "#94a3b8" }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}
